import { DestroyRef, Injectable } from '@angular/core';
import { Observable, merge } from 'rxjs';
import { ResourceType } from 'src/app/models/api';
import { SendEmailApiModel } from 'src/app/models/api/actions/sendEmailApi.model';
import { HttpService } from '../http/http.service';
import {
  ListUserPermissionsActionOutputs,
  RemoveUserPermissionsActionInputs,
  RemoveUserPermissionsActionOutputs,
} from 'src/app/models/api/actions/userPermissions.model';
import {
  DownloadAttachmentActionInputs as DownloadResourceAttachmentActionInputs,
  DownloadAttachmentActionOutputs as DownloadResourceAttachmentActionOutputs,
} from 'src/app/models/api/actions/downloadAttachment.model';
import { JsonHalCollectionItems, JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { ActionTypes } from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { HttpParams } from '@angular/common/http';
import { ActionGetRawResourceOutputs } from 'src/app/models/api/actions/getResourceDefinition.model';
import {
  PowerplatformSetCopilotAuthenticationActionInputs,
  PowerplatformSetCopilotAuthenticationActionOutputs,
} from 'src/app/models/api/actions/powerplatformSetCopilotAuthenticaiton.model';
import { PowerplatformCopilotAuthenticationTypeEnum } from 'src/app/components/actions/powerplatform-actions/set-copilot-authentication/enums/enums';
import { ResourceStatus } from 'src/app/models/api/inventoryResource.model';
import { ActionGetRawListOutputs } from 'src/app/models/api/actions/getRawJsonList.model';
import {
  ZenityEntityActionOutputs,
  ZenityEntityActionResultCode,
} from 'src/app/models/api/actions/zenityEntityActions';
import { GetFileActionOutputs } from '../../../models/api/actions/getFile.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InventorySupportedActions } from '../../actions/inventory/inventorySupportedActions';
import { AutonomousWithTriggersPayload } from '../../../components/playbooks/playbook-action/common/interfaces/autonomous-with-triggers.payload';
import { ZenityEntityActionInputs } from 'src/app/models/api/actions/zenityEntityActions';

export interface ActionDeleteResponseModel {
  alreadyDeleted: boolean;
}

export interface ActionGetResourceDefinitionResponseModel {
  rawJson: string;
}

export interface ActionGetListResourceDefinitionResponseModel {
  raw_json_items: string[];
}

export interface RequiredParamsForActionsHttpRequests {
  integrationId: string;
  resourceId: string;
  resourceType: ResourceType;
  isAdminEndpoint?: boolean;
  resourceStatus?: ResourceStatus;
}

export interface RequiredParamsForAgentStepHttpRequests extends RequiredParamsForActionsHttpRequests {
  stepId: string;
}

export interface RequiredParamsForSetAccessHttpRequests extends RequiredParamsForActionsHttpRequests {
  isChangeAccessActionEnabled: boolean;
}

export interface RequiredParamsForGetActionsOfResourcesRequest {
  integrationId: string;
  resourcesIds: string[];
}

export interface RequiredParamsForGetActionsOfAgentStepsRequest {
  integrationId: string;
  stepIds: string[];
}

export interface RequiredParamsForGetActionsOfViolationsRequest {
  integrationId: string;
  violationsIds: string[];
}

export interface RequiredParamsForViolationActionsHttpRequests extends RequiredParamsForActionsHttpRequests {
  violationId: string;
}

export interface RequiredParamsForChatGptEnterpriseDeleteConversationAction
  extends RequiredParamsForActionsHttpRequests {
  integrationId: string;
  stepId: string;
}

export interface AddOrRemoveOwnerParamsModel extends RequiredParamsForActionsHttpRequests {
  isAddOwner: boolean;
}

export interface SetApplicationQuarantineStateParamsModel extends RequiredParamsForActionsHttpRequests {
  quarantine_status: PpApplicationQuarantineStatus;
}

export interface AutomationActivationParams extends RequiredParamsForActionsHttpRequests {
  action: AutomationActivationActions;
}

export enum AutomationActivationActions {
  start = 'start',
  stop = 'stop',
}

export enum PpApplicationQuarantineStatus {
  quarantined = 'quarantined',
  unquarantined = 'unquarantined',
}

export enum ActionResultCode {
  success = 'success',
  failure = 'failure',
  no_effect = 'noEffect',
}
export interface ResourceActionOutput {
  resultCode: ActionResultCode;
  title: string;
  description: string | null;
}

export interface IActionTypeResponse {
  actionType: ActionTypes;
  isRestricted: boolean;
  restrictedReason: string;
}
export interface ActionResponseModel {
  id: string;
  title: string;
  description: string;
  requiredPermissions?: string[];
}
export interface ResourceActionsResponseModel {
  resourceId: string;
  integrationId: string;
  actions: ActionResponseModel[];
}

export interface AgentStepActionsResponseModel {
  stepId: string;
  integrationId: string;
  actions: ActionResponseModel[];
}

export interface ViolationsActionsResponseModel {
  violationId: string;
  integrationId: string;
  actions: ActionResponseModel[];
}

export enum ActionStatus {
  enabled = 'enabled',
  disabled = 'disabled',
}

export enum PowerplatformAgentActionAuthenticationMode {
  maker = 'maker', // "author"
  invoker = 'invoker', // "user"
}

export enum PowerplatformAgentActionConfirmationMode {
  strict = 'strict',
  disabled = 'disabled',
}

export interface PowerplatformAgentActionSetAuthenticationModePayload extends AutonomousWithTriggersPayload {
  authenticationMode: PowerplatformAgentActionAuthenticationMode;
}

export interface PowerplatformAgentActionSetConfirmationModePayload extends AutonomousWithTriggersPayload {
  confirmationMode: PowerplatformAgentActionConfirmationMode;
}

export interface PowerplatformAgentActionSetWebBrowsingPayload {
  isWebBrowsingEnabled: boolean;
}

export interface PowerplatformEnvironmentSetAuditPayload {
  isAuditEnabled: boolean;
}

export type IdentityResourceIdKind = 'platformResourceId' | 'inventoryResourceId';

export enum FabricAccessLevel {
  admin = 'Admin',
  member = 'Member',
  contributor = 'Contributor',
  viewer = 'Viewer',
}

export interface SetFabricResourcePermissionsActionInputs extends ZenityEntityActionInputs {
  identity_resource_type: 'user';
  identity_resource_id_kind: IdentityResourceIdKind;
  identity_resource_id: string;
  access_level: FabricAccessLevel;
}

export type SetResourcePermissionsActionOutputs = ZenityEntityActionOutputs;

export interface RemoveResourcePermissionsActionInputs extends ZenityEntityActionInputs {
  identity_platform_resource_id: string;
}

export type RemoveResourcePermissionsActionOutputs = ZenityEntityActionOutputs;

export interface SetAllUsersAccessActionInputs extends ZenityEntityActionInputs {
  accessLevel: FabricAccessLevel;
}

export type SetAllUsersAccessActionOutputs = ZenityEntityActionOutputs;

export type IsolateResourceActionOutputs = ZenityEntityActionOutputs;

@Injectable({
  providedIn: 'root',
})
export class ActionsHttpServiceService {
  private readonly _actionsBaseUrl: string = 'actions';

  constructor(
    private readonly _httpService: HttpService,
    private readonly _destroyRef: DestroyRef
  ) {}

  // #region Public API Methods

  public getActionsOfPlaybookTrigger(
    integrationId: string,
    trigger: PlaybookTrigger
  ): Observable<JsonHalCollectionItems<IActionTypeResponse>> {
    const url = `playbooks/actions?integrationId=${integrationId}`;
    return this._httpService.post<PlaybookTrigger, JsonHalCollectionItems<IActionTypeResponse>>(url, trigger);
  }

  public getActionsOfResources(
    params: RequiredParamsForGetActionsOfResourcesRequest
  ): Observable<JsonHalCollection<ResourceActionsResponseModel>> {
    // URL is v1/inventory/integrations/{integrationId}/resources/actions and the query params is resources ids of the integration and looks like this: ?resourceIds={resourceId1},{resourceId2}
    const url = `inventory/integrations/${params.integrationId}/resources/actions`;
    const httpParams = new HttpParams().append('resourcesIds', params.resourcesIds.join(','));
    return this._httpService.get<JsonHalCollection<ResourceActionsResponseModel>>(url, undefined, httpParams);
  }

  public getActionsOfViolations(
    params: RequiredParamsForGetActionsOfViolationsRequest
  ): Observable<JsonHalCollection<ViolationsActionsResponseModel>> {
    // URL is v1/assessment/integrations/{integrationId}/violations/actions and the query params is violations ids of the integration and looks like this: ?violationsIds={violationId1},{violationId2}
    const url = `assessment/integrations/${params.integrationId}/violations/actions`;
    const httpParams = new HttpParams().append('violationsIds', params.violationsIds.join(','));
    return this._httpService.get<JsonHalCollection<ViolationsActionsResponseModel>>(url, undefined, httpParams);
  }

  public getActionsOfAgentSteps(
    params: RequiredParamsForGetActionsOfAgentStepsRequest
  ): Observable<JsonHalCollection<AgentStepActionsResponseModel>> {
    // URL is v1-beta/detection/integrations/{integrationId}/agentSteps/actions and the query params is step ids of the integration and looks like this: ?stepIds={stepId1},{stepId2}
    const url = `detection/integrations/${params.integrationId}/agentSteps/actions`;
    const httpParams = new HttpParams().append('stepIds', params.stepIds.join(','));
    return this._httpService.get<JsonHalCollection<AgentStepActionsResponseModel>>(url, undefined, httpParams, true);
  }

  public addOwner(params: RequiredParamsForActionsHttpRequests, email: string): Observable<boolean> {
    const url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}/owner`;
    return this._httpService.post<{ email: string }, boolean>(url, {
      email: email,
    });
  }

  public removeOwner(params: RequiredParamsForActionsHttpRequests, email: string): Observable<boolean> {
    const url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}/owner`;
    return this._httpService.delete_with_body<{ email: string }, boolean>(url, { email: email });
  }

  public secureFlowSteps(params: RequiredParamsForViolationActionsHttpRequests): Observable<ResourceActionOutput> {
    const url = `assessment/integrations/${params.integrationId}/violations/${params.violationId}/actions/powerplatformSecureFlowSteps:execute`;
    return this._httpService.post(url, {});
  }

  public getFlowDefinition(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ActionGetResourceDefinitionResponseModel> {
    const url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}/getDefinition`;
    return this._httpService.get<ActionGetResourceDefinitionResponseModel>(url);
  }

  public getRawResource(integrationId: string, resourceId: string): Observable<ActionGetRawResourceOutputs> {
    return this._executeInventoryResourceAction(integrationId, resourceId, 'getRawResource', '');
  }

  public getFlowRuns(integrationId: string, resourceId: string): Observable<ActionGetRawListOutputs> {
    return this._executeInventoryResourceAction(integrationId, resourceId, 'powerplatformGetRawFlowRuns', '');
  }

  public deleteResource(params: RequiredParamsForActionsHttpRequests): Observable<ActionDeleteResponseModel> {
    let url = `${this._actionsBaseUrl}/integration/${params.integrationId}/resource/${params.resourceType}/${params.resourceId}`;

    if (params.resourceType === ResourceType.recipe) {
      url = `${this._getBaseUrl(params)}/integration/${params.integrationId}/recipe/${params.resourceId}`;
    }
    return this._httpService.delete<ActionDeleteResponseModel>(url);
  }

  // #region Agent Actions
  public agentDeleteAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'powerplatformAgentDeleteAction'
    );
  }

  public agentActionDeleteAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'powerplatformAgentActionDeleteAction'
    );
  }

  public agentActionEnforceUserAuthenticationAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'powerplatformAgentActionEnforceUserAuthenticationAction'
    );
  }

  public agentActionEnforceUserConfirmationAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'powerplatformAgentActionEnforceUserConfirmationAction'
    );
  }

  public agentActionEnableAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      InventorySupportedActions.PowerplatformAgentActionEnableAction
    );
  }

  public agentActionDisableAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      InventorySupportedActions.PowerplatformAgentActionDisableAction
    );
  }

  public agentActionUpdateAuthenticationMode(
    params: RequiredParamsForActionsHttpRequests,
    authenticationMode: PowerplatformAgentActionAuthenticationMode,
    ignoreIfAutonomousWithTriggers?: boolean
  ): Observable<ZenityEntityActionOutputs> {
    const body: PowerplatformAgentActionSetAuthenticationModePayload = {
      authenticationMode: authenticationMode,
      ignoreIfAutonomousWithTriggers: ignoreIfAutonomousWithTriggers,
    };
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      InventorySupportedActions.PowerplatformAgentActionSetAuthenticationModeAction,
      body
    );
  }

  public agentActionUpdateConfirmationMode(
    params: RequiredParamsForActionsHttpRequests,
    confirmationMode: PowerplatformAgentActionConfirmationMode,
    ignoreIfAutonomousWithTriggers?: boolean
  ): Observable<ZenityEntityActionOutputs> {
    const request: PowerplatformAgentActionSetConfirmationModePayload = {
      confirmationMode: confirmationMode,
      ignoreIfAutonomousWithTriggers: ignoreIfAutonomousWithTriggers,
    };
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      InventorySupportedActions.PowerplatformAgentActionSetConfirmationModeAction,
      request
    );
  }

  public agentActionUpdateWebBrowsing(
    params: RequiredParamsForActionsHttpRequests,
    isWebBrowsingEnabled: boolean
  ): Observable<ZenityEntityActionOutputs> {
    const body: PowerplatformAgentActionSetWebBrowsingPayload = {
      isWebBrowsingEnabled: isWebBrowsingEnabled,
    };
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      InventorySupportedActions.PowerplatformAgentSetWebBrowsingAction,
      body
    );
  }

  public environmentSetAudit(
    params: RequiredParamsForActionsHttpRequests,
    isAuditEnabled: boolean
  ): Observable<ZenityEntityActionOutputs> {
    const body: PowerplatformEnvironmentSetAuditPayload = {
      isAuditEnabled: isAuditEnabled,
    };
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      InventorySupportedActions.PowerplatformEnvironmentSetAuditAction,
      body
    );
  }
  // #endregion

  public setResourceQuarantineState(params: SetApplicationQuarantineStateParamsModel): Observable<boolean> {
    const url = `${this._actionsBaseUrl}/integration/${params.integrationId}/application/${params.resourceId}/quarantine-state?status=${params.quarantine_status}`;

    return this._httpService.put<boolean>(url);
  }

  // #region ChatGPT Actions
  public gptDeleteAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseDeleteGptAction'
    );
  }

  public gptIsolateAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseIsolateGptAction'
    );
  }

  public isolateGptPlaybookAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseIsolateGptAction'
    );
  }

  public gptDeleteGptKnowledgeAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseDeleteGptKnowledgeAction'
    );
  }

  public gptGetKnowledgeFileUrlAction(params: RequiredParamsForActionsHttpRequests): Observable<GetFileActionOutputs> {
    const response = this._executeInventoryResourceAction<object, GetFileActionOutputs>(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseGetGptKnowledgeFileUrlAction',
      {}
    );
    response.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((res) => {
      if (res.resultCode === ZenityEntityActionResultCode.success) {
        window.open(res.url, '_blank');
      }
    });
    return response;
  }

  public gptUserCanvasDeleteAction(
    params: RequiredParamsForActionsHttpRequests
  ): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseDeleteUserCanvasAction'
    );
  }

  public gptGetUserCanvasAction(params: RequiredParamsForActionsHttpRequests): Observable<ZenityEntityActionOutputs> {
    return this._executeInventoryResourceAction(
      params.integrationId,
      params.resourceId,
      'chatGptEnterpriseGetUserCanvasAction'
    );
  }

  public gptDeleteConversationAction(
    params: RequiredParamsForChatGptEnterpriseDeleteConversationAction
  ): Observable<ZenityEntityActionOutputs> {
    return this._executeDetectionAgentStepAction(
      params.integrationId,
      params.stepId,
      'chatGptEnterpriseDeleteConversationAction'
    );
  }
  // #endregion

  public automationActivation(params: AutomationActivationParams, resourceType: ResourceType): Observable<boolean> {
    const resourceTypeStr = resourceType == ResourceType.flow ? 'flow' : 'recipe';
    const url = `${this._actionsBaseUrl}/integration/${params.integrationId}/${resourceTypeStr}/${params.resourceId}?action=${params.action}`;

    return this._httpService.put<boolean>(url);
  }

  public sendEmail(integrationId: string, emailData: SendEmailApiModel): Observable<boolean> {
    const url = `${this._actionsBaseUrl}/send-email/integration/${integrationId}`;

    return this._httpService.post<SendEmailApiModel, boolean>(url, emailData);
  }

  public getUserAccessDetails(integrationId: string, resourceId: string): Observable<ListUserPermissionsActionOutputs> {
    return this._executeInventoryResourceAction(integrationId, resourceId, 'listResourcePermissions', '');
  }

  public removeUserAccess(
    integrationId: string,
    resourceId: string,
    userIds: string[]
  ): Observable<RemoveUserPermissionsActionOutputs> {
    const observables = userIds.map((userId) =>
      this._executeInventoryResourceAction<RemoveUserPermissionsActionInputs, RemoveUserPermissionsActionOutputs>(
        integrationId,
        resourceId,
        'removeResourcePermissions',
        { identity_platform_resource_id: userId }
      )
    );
    return merge(...observables);
  }

  public setResourcePermissions(
    integrationId: string,
    resourceId: string,
    inputs: SetFabricResourcePermissionsActionInputs
  ): Observable<SetResourcePermissionsActionOutputs> {
    return this._executeInventoryResourceAction<
      SetFabricResourcePermissionsActionInputs,
      SetResourcePermissionsActionOutputs
    >(integrationId, resourceId, 'setResourcePermissions', inputs);
  }

  public removeResourcePermissions(
    integrationId: string,
    resourceId: string,
    email: string
  ): Observable<RemoveResourcePermissionsActionOutputs> {
    const inputs: RemoveResourcePermissionsActionInputs = {
      identity_platform_resource_id: email,
    };
    return this._executeInventoryResourceAction<
      RemoveResourcePermissionsActionInputs,
      RemoveResourcePermissionsActionOutputs
    >(integrationId, resourceId, 'removeResourcePermissions', inputs);
  }

  public addUserAccess(
    integrationId: string,
    resourceId: string,
    inputs: SetFabricResourcePermissionsActionInputs
  ): Observable<SetResourcePermissionsActionOutputs> {
    return this._executeInventoryResourceAction<
      SetFabricResourcePermissionsActionInputs,
      SetResourcePermissionsActionOutputs
    >(integrationId, resourceId, 'powerBiWorkspaceAddUserAccess', inputs);
  }

  public isolateResource(integrationId: string, resourceId: string): Observable<IsolateResourceActionOutputs> {
    return this._executeInventoryResourceAction<object, IsolateResourceActionOutputs>(
      integrationId,
      resourceId,
      'isolateResource'
    );
  }

  public setAllUsersAccess(
    integrationId: string,
    resourceId: string,
    accessLevel: FabricAccessLevel
  ): Observable<SetAllUsersAccessActionOutputs> {
    const inputs: SetAllUsersAccessActionInputs = { accessLevel };
    return this._executeInventoryResourceAction<SetAllUsersAccessActionInputs, SetAllUsersAccessActionOutputs>(
      integrationId,
      resourceId,
      'setAllUsersAccess',
      inputs
    );
  }

  public downloadAttachment(
    integrationId: string,
    resourceId: string,
    attachmentId: string
  ): Observable<DownloadResourceAttachmentActionOutputs> {
    const body: DownloadResourceAttachmentActionInputs = {
      attachmentId: attachmentId,
    };
    return this._executeInventoryResourceAction(integrationId, resourceId, 'downloadResourceAttachment', body);
  }

  public powerplatformSetCopilotAuthentication(
    integrationId: string,
    resourceId: string,
    copilotAuthentication: PowerplatformCopilotAuthenticationTypeEnum
  ): Observable<PowerplatformSetCopilotAuthenticationActionOutputs> {
    const body: PowerplatformSetCopilotAuthenticationActionInputs = {
      authenticationType: copilotAuthentication,
    };
    return this._executeInventoryResourceAction(
      integrationId,
      resourceId,
      'powerplatformSetCopilotAuthentication',
      body
    );
  }

  public powerplatformPublishCopilot(
    integrationId: string,
    resourceId: string
  ): Observable<PowerplatformSetCopilotAuthenticationActionOutputs> {
    return this._executeInventoryResourceAction(integrationId, resourceId, 'powerplatformPublishCopilot', '');
  }

  // #endregion

  // #region Private Helper Methods

  /**
   * Executes an action on an inventory resource
   * @param integrationId The integration ID
   * @param resourceId The resource ID
   * @param actionName The action name (without ':execute' suffix)
   * @param body Optional request body (defaults to empty object)
   * @returns Observable of the action output
   */
  private _executeInventoryResourceAction<TInput = object, TOutput = ZenityEntityActionOutputs>(
    integrationId: string,
    resourceId: string,
    actionName: string,
    body: TInput = {} as TInput
  ): Observable<TOutput> {
    const url = `inventory/integrations/${integrationId}/resources/${resourceId}/actions/${actionName}:execute`;
    return this._httpService.post<TInput, TOutput>(url, body);
  }

  /**
   * Executes an action on a detection agent step
   * @param integrationId The integration ID
   * @param stepId The step ID
   * @param actionName The action name (without ':execute' suffix)
   * @param body Optional request body (defaults to empty object)
   * @returns Observable of the action output
   */
  private _executeDetectionAgentStepAction<TInput = object, TOutput = ZenityEntityActionOutputs>(
    integrationId: string,
    stepId: string,
    actionName: string,
    body: TInput = {} as TInput
  ): Observable<TOutput> {
    const url = `detection/integrations/${integrationId}/agentSteps/${stepId}/actions/${actionName}:execute`;
    return this._httpService.post<TInput, TOutput>(url, body, undefined, undefined, true);
  }

  private _getBaseUrl(params: RequiredParamsForActionsHttpRequests): string {
    let appBaseUrl = `${this._actionsBaseUrl}`;

    if (params.isAdminEndpoint) {
      appBaseUrl = `${this._actionsBaseUrl}/admin`;
    }

    return appBaseUrl;
  }

  // #endregion
}
